import {
  compileShortcuts,
  TextShortcut,
  TEXT_SHORTCUTS
} from "./shortcuts";

/**
 * Native-DOM auto-replace for text shortcuts — the counterpart to the TipTap
 * `TextShortcuts` extension, reading the same TEXT_SHORTCUTS map.
 *
 * A single capture-phase `beforeinput` listener on `document` covers every
 * plain <input>/<textarea> in the app (including react-textarea-autosize and
 * any future field) from one place — no per-component wiring. TipTap editors
 * are contentEditable (not input/textarea) so they're untouched here and
 * handled by their extension instead.
 *
 * Gotchas, all handled:
 * - Fires on completion: keyed off the char being inserted (`event.data`).
 * - Caret: setRangeText(..., "end") leaves it right after the inserted glyph.
 * - React sync: after setRangeText we dispatch an InputEvent (inputType
 *   "insertText") so controlled inputs' onChange fires and state stays in sync.
 * - IME / Hebrew / CJK: bails on `event.isComposing` and only acts on
 *   `inputType === "insertText"`.
 * - Paste/drop: those are `insertFromPaste`/`insertFromDrop`, so ignored.
 * - Structured fields (number/email/tel/password/url/date…) are excluded so a
 *   replacement can never corrupt a value; add `data-no-text-shortcuts` to any
 *   element (or ancestor) to opt out explicitly.
 */

const EXCLUDED_INPUT_TYPES = new Set([
  "search",
  "number",
  "email",
  "tel",
  "password",
  "url",
  "date",
  "time",
  "datetime-local",
  "month",
  "week",
  "color",
  "range",
  "file",
  "checkbox",
  "radio",
  "hidden"
]);

const isEditableTarget = (
  target: EventTarget | null
): target is HTMLInputElement | HTMLTextAreaElement => {
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) {
    return !EXCLUDED_INPUT_TYPES.has((target.type || "text").toLowerCase());
  }
  return false;
};

/**
 * Install the global listener. Returns a cleanup function that removes it.
 * Safe to call once at app startup.
 */
export const installNativeTextShortcuts = (
  shortcuts: TextShortcut[] = TEXT_SHORTCUTS
): (() => void) => {
  const rules = compileShortcuts(shortcuts);

  const onBeforeInput = (event: Event) => {
    const inputEvent = event as InputEvent;
    if (inputEvent.isComposing) return;
    if (inputEvent.inputType !== "insertText") return;

    const data = inputEvent.data;
    if (!data) return;

    const element = inputEvent.target;
    if (!isEditableTarget(element)) return;
    if (element.closest("[data-no-text-shortcuts]")) return;

    const { selectionStart, selectionEnd, value } = element;
    // Only a collapsed caret — skip when a range is selected.
    if (selectionStart === null || selectionStart !== selectionEnd) return;

    const caret = selectionStart;
    const candidate = value.slice(0, caret) + data;
    const match = rules.find((rule) => candidate.endsWith(rule.trigger));
    if (!match) return;

    // Chars already before the caret that belong to the trigger; the rest is
    // `data`, the char being typed right now (which we're about to cancel).
    const prefixLength = match.trigger.length - data.length;
    if (prefixLength < 0) return;
    const start = caret - prefixLength;

    inputEvent.preventDefault();
    // Replace the trigger in place and notify React with an InputEvent so
    // controlled inputs stay in sync (a bare Event would leave form state
    // showing the typed chars instead of the glyph). Caret lands after the
    // glyph via the "end" selection mode.
    element.setRangeText(match.replace, start, caret, "end");
    element.dispatchEvent(
      new InputEvent("input", {
        bubbles: true,
        inputType: "insertText",
        data: match.replace
      })
    );
  };

  // Scope to the app root so third-party widgets (Pendo/Sentry) injected as
  // siblings of #root aren't affected. Capture phase so we run before React's
  // handlers and can cancel the input.
  const root = document.getElementById("root") ?? document;
  root.addEventListener("beforeinput", onBeforeInput, true);
  return () => root.removeEventListener("beforeinput", onBeforeInput, true);
};

export default installNativeTextShortcuts;
